import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useEnvironmentInfo } from "./EnvironmentSwitcher";
import AuthDiagnosticsPanel from "./AuthDiagnosticsPanel";
import { useGlobal } from "@/context/GlobalContext";
import { authApi } from "@/services/api";
import {
    Home,
    Search,
    LockKeyhole,
    Mail,
    Key,
    GraduationCap,
    MailCheck,
    LayoutDashboard,
    User as UserIcon,
    Calendar,
    DollarSign,
    FolderOpen,
    RefreshCw,
    RotateCcw,
} from "lucide-react";

import { TestUser } from "@/core/models/auth/TestUser";
import DevToolsSidebarLoadingDemo from "./DevToolsSidebarLoadingDemo";
import { isProductionEnvironment } from "@/utils/devTools";
// Define navigation shortcuts
interface NavigationShortcut {
    label: string;
    path: string;
    description?: string;
    category: "pages" | "forms" | "auth" | "enrollment";
    icon?: React.ReactNode;
    requiresAuth?: boolean;
    requiresUnverified?: boolean;
}

const navigationShortcuts: NavigationShortcut[] = [
    // Authentication & Enrollment Portal Forms
    {
        label: "Home/Login",
        path: "/",
        description: "Enrollment portal landing page",
        category: "auth",
        icon: <Home className="h-4 w-4" />,
    },
    {
        label: "Check Account",
        path: "/?view=checkAccount",
        description: "Check if account exists",
        category: "enrollment",
        icon: <Search className="h-4 w-4" />,
    },
    {
        label: "Login Form",
        path: "/?view=login",
        description: "Login to existing account",
        category: "enrollment",
        icon: <LockKeyhole className="h-4 w-4" />,
    },

    // Registration - Email Flow
    {
        label: "Register - Email (Step 2)",
        path: "/?view=register&type=email",
        description: "Email registration - Personal info form",
        category: "enrollment",
        icon: <Mail className="h-4 w-4" />,
    },
    {
        label: "Register - Email (Step 3)",
        path: "/?view=register&type=email&step=3",
        description: "Email registration - Password creation form",
        category: "enrollment",
        icon: <Key className="h-4 w-4" />,
    },

    // Registration - Student Number Flow
    {
        label: "Register - Student #",
        path: "/?view=register&type=student",
        description: "Student number registration - Password only",
        category: "enrollment",
        icon: <GraduationCap className="h-4 w-4" />,
    },

    // Email Verification
    {
        label: "Email Verification",
        path: "/?view=verify&email=test@example.com",
        description: "Email verification code entry form",
        category: "enrollment",
        icon: <MailCheck className="h-4 w-4" />,
    },

    // Post-Registration View
    {
        label: "After Registration (Unverified)",
        path: "/dashboard",
        description:
            "Dashboard view after registration (requires unverified user)",
        category: "enrollment",
        icon: <LayoutDashboard className="h-4 w-4" />,
        requiresAuth: true,
        requiresUnverified: true,
    },

    // Dashboard
    {
        label: "Dashboard",
        path: "/dashboard",
        description: "Main dashboard",
        category: "pages",
        icon: <LayoutDashboard className="h-4 w-4" />,
        requiresAuth: true,
    },

    // Profile Forms
    {
        label: "Profile - Personal",
        path: "/dashboard/profile?step=personal",
        description: "Personal information form",
        category: "forms",
        icon: <UserIcon className="h-4 w-4" />,
        requiresAuth: true,
    },
    {
        label: "Profile - Interview",
        path: "/dashboard/profile?step=admissionsInterview",
        description: "Admissions interview scheduling",
        category: "forms",
        icon: <Calendar className="h-4 w-4" />,
        requiresAuth: true,
    },

    // Other Pages
    {
        label: "Program Enrollment",
        path: "/dashboard/program-enrollment",
        description: "Select degree program",
        category: "pages",
        icon: <GraduationCap className="h-4 w-4" />,
        requiresAuth: true,
    },
    {
        label: "Financial Aid",
        path: "/dashboard/financial",
        description: "Financial assistance application",
        category: "pages",
        icon: <DollarSign className="h-4 w-4" />,
        requiresAuth: true,
    },
    {
        label: "Documents",
        path: "/dashboard/documents",
        description: "Upload and manage documents",
        category: "pages",
        icon: <FolderOpen className="h-4 w-4" />,
        requiresAuth: true,
    },
];

export function DevInfoPanel() {
    const envInfo = useEnvironmentInfo();
    const { getUser, getIsVerified, refreshUser, godMode, setGodMode } = useGlobal();
    const user = getUser();
    const isVerified = getIsVerified();
    const [showAuthDiagnostics, setShowAuthDiagnostics] = useState(false);
    const [showEmailPopupToggle, setShowEmailPopupToggle] = useState(false);
    const [inlineTestUsers, setInlineTestUsers] = useState<TestUser[]>([]);
    const [loadingInlineUsers, setLoadingInlineUsers] = useState(false);
    const [inlineUserError, setInlineUserError] = useState<string>("");
    const [selectedTestUserEmail, setSelectedTestUserEmail] =
        useState<string>("");
    const [loggingIn, setLoggingIn] = useState(false);
    const navigate = useNavigate();
    const location = useLocation();
    // Environment detection — this component is itself only ever mounted behind isDevToolsEnabled()
    // (see App.tsx's DevTaskBar), so this just needs to agree with that same single source of truth
    // rather than recomputing its own, differently-scoped notion of "dev-like."
    const isTestEnvironment = !isProductionEnvironment();
  
    useEffect(() => {
     
  
       
        if (isTestEnvironment && inlineTestUsers.length === 0 && !user) {
        
            loadInlineTestUsers();
        }
    }, [isTestEnvironment, user, inlineTestUsers.length]);

    async function loadInlineTestUsers() {
        setLoadingInlineUsers(true);
        setInlineUserError("");
        try {
            const users = await authApi.getTestUsers();
         

            if (!users || users.length === 0) {
                setInlineUserError(
                    "No @testsep.com test users found in the database"
                );
            } else {
                setInlineTestUsers(users || undefined);
            }
        } catch (e: any) {
            console.error("❌ Failed to load test users:", e);
            setInlineUserError(e?.message || "Failed to load test users");
        } finally {
            setLoadingInlineUsers(false);
        }
    }

    async function handleQuickAutoAuth(email: string) {
        if (!email) return;

        setLoggingIn(true);
        try {
            // Server-side passwordless test login (Development host only) -- no shared
            // test-account password is compiled into the bundle.
            await authApi.autoAuthenticate(email);
            await refreshUser();
           
        } catch (e: any) {
            console.error("❌ Quick dropdown auto-auth failed", e);
            setInlineUserError(e?.message || "Auto-authentication failed");
        } finally {
            setLoggingIn(false);
        }
    }

    const handleNavigate = (path: string) => navigate(path);
    const isCurrentPath = (path: string) =>
        location.pathname + location.search === path ||
        location.pathname === path;

    const handleToggleEmailPopup = () => {
        window.dispatchEvent(
            new CustomEvent("devtools:showEmailPopup", {
                detail: { forceShow: true },
            })
        );
        setShowEmailPopupToggle(true);
        setTimeout(() => setShowEmailPopupToggle(false), 1000);
    };

    const groupedShortcuts = {
        auth: navigationShortcuts.filter((s) => s.category === "auth"),
        enrollment: navigationShortcuts.filter(
            (s) => s.category === "enrollment"
        ),
        pages: navigationShortcuts.filter((s) => s.category === "pages"),
        forms: navigationShortcuts.filter((s) => s.category === "forms"),
    };

    return (
        <>
            <DevToolsSidebarLoadingDemo />
            <div className="text-slate-200 border-slate-700 bg-slate-900/70 space-y-4 rounded-lg border p-4 text-sm">
                {/* God Mode toggle */}
                <div className="bg-yellow-500/10 border-yellow-400/30 rounded border p-2">
                    <div className="mb-1 flex items-center justify-between">
                        <span className="text-yellow-200 font-semibold text-[11px] uppercase tracking-wide">
                            God Mode
                        </span>
                        <span className={`text-[10px] font-bold ${godMode ? "text-yellow-300" : "text-slate-500"}`}>
                            {godMode ? "ON" : "OFF"}
                        </span>
                    </div>
                    <button
                        onClick={() => setGodMode(!godMode)}
                        className={`w-full flex items-center justify-center gap-2 px-3 py-2 rounded text-xs font-medium transition ${
                            godMode
                                ? "bg-yellow-400 hover:bg-yellow-300 text-slate-900"
                                : "bg-slate-700/60 hover:bg-slate-700 text-slate-300"
                        }`}
                        title="Bypasses profile-completion guards — all sidebar pages and panels become accessible"
                    >
                        {godMode ? "⚡ Disable God Mode" : "☁ Enable God Mode"}
                    </button>
                    <p className="text-slate-500 mt-1 text-[10px]">
                        Unlocks all sidebar pages, panels, and skips first-visit redirects.
                    </p>
                </div>

                {/* Quick Login moved to very top */}
                {!user && isTestEnvironment && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <label className="text-slate-400 uppercase tracking-wide text-[11px]">
                                Quick Login Test User
                            </label>
                            <button
                                onClick={loadInlineTestUsers}
                                disabled={loadingInlineUsers}
                                className="text-slate-400 transition-colors hover:text-slate-200"
                                title="Refresh test users list"
                            >
                                <RefreshCw
                                    className={`w-3 h-3 ${loadingInlineUsers ? "animate-spin" : ""}`}
                                />
                            </button>
                        </div>
                        <div className="flex flex-col gap-2">
                            <select
                                value={selectedTestUserEmail}
                                onChange={(e) => {
                                    setSelectedTestUserEmail(e.target.value);
                                    setInlineUserError(""); // Clear error on selection
                                }}
                                disabled={loggingIn || loadingInlineUsers}
                                className="bg-slate-800 border border-slate-700 rounded px-2 py-2 text-xs text-slate-200 focus:outline-none focus:ring-1 focus:ring-ecpi-blue disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                <option value="">Select test user...</option>
                                {inlineTestUsers.map((u) => (
                                    <option key={u.applicantId} value={u.email}>
                                        {u.firstName} {u.lastName}{" "}
                                        {u.isVerified ? "✓" : "✗"} - {u.email}
                                    </option>
                                ))}
                            </select>
                            <button
                                disabled={
                                    !selectedTestUserEmail ||
                                    loggingIn ||
                                    loadingInlineUsers
                                }
                                onClick={async () => {
                                    if (selectedTestUserEmail)
                                        await handleQuickAutoAuth(
                                            selectedTestUserEmail
                                        );
                                }}
                                className={`px-3 py-2 rounded text-xs font-medium transition-colors items-center gap-1 ${
                                    !selectedTestUserEmail ||
                                    loggingIn ||
                                    loadingInlineUsers
                                        ? "bg-slate-700 text-slate-500 cursor-not-allowed"
                                        : "bg-purple-600 hover:bg-purple-700 text-white"
                                }`}
                            >
                                {loggingIn ? (
                                    <>
                                        <RefreshCw className="h-3 w-3 animate-spin" />
                                        <span>Logging in...</span>
                                    </>
                                ) : (
                                    <span>Login</span>
                                )}
                            </button>
                        </div>
                        {loadingInlineUsers && (
                            <div className="text-slate-500 flex items-center gap-2 text-[10px]">
                                <RefreshCw className="h-3 w-3 animate-spin" />
                                <span>Loading test users...</span>
                            </div>
                        )}
                        {inlineUserError && (
                            <div className="text-red-400 bg-red-900/20 border-red-800 rounded border px-2 py-1 text-[10px]">
                                {inlineUserError}
                            </div>
                        )}
                        {!loadingInlineUsers &&
                            !inlineUserError &&
                            inlineTestUsers.length > 0 && (
                                <p className="text-slate-500 text-[10px]">
                                    {inlineTestUsers.length} @testsep.com test
                                    user
                                    {inlineTestUsers.length !== 1
                                        ? "s"
                                        : ""}{" "}
                                    available. ✓ = verified, ✗ = unverified
                                </p>
                            )}
                        {!loadingInlineUsers &&
                            inlineTestUsers.length === 0 &&
                            !inlineUserError && (
                                <p className="text-yellow-400 text-[10px]">
                                    No test users found. Create users with
                                    @testsep.com emails.
                                </p>
                            )}
                    </div>
                )}

                {/* First Visit Reset (dev only) */}
                {user && isTestEnvironment && (
                    <div className="bg-purple-500/10 border-purple-400/30 mb-2 rounded border p-2">
                        <div className="mb-1 flex items-center justify-between">
                            <span className="text-purple-200 font-semibold text-[11px]">
                                First Visit Flag
                            </span>
                            <span className="text-slate-400 text-[10px]">
                                {user?.firstVisit ? "true" : "false"}
                            </span>
                        </div>
                        <button
                            onClick={async () => {
                                try {
                                    // Prefer auth-scoped endpoint that uses the current user's claims
                                    await authApi.resetFirstVisit();
                                   
                                    await refreshUser();   
                                    window.location.reload();
                                } catch (e) {
                                    console.error("Reset first visit error", e);
                                }
                            }}
                            className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded bg-purple-600/60 hover:bg-purple-600 text-white text-xs font-medium transition"
                            title="Reset FirstVisit flag to true for current user"
                        >
                            <RotateCcw className="h-3 w-3" /> Reset First Visit
                        </button>
                    </div>
                )}

                {/* Dev Quick Navigation */}
                <div>
                    <p className="text-white mb-2 font-bold">
                        Dev Quick Navigation
                    </p>
                    <p className="text-slate-400 mb-3 text-xs">
                        Jump to any page or form instantly without normal
                        navigation flow
                    </p>
                    {/* Auth Section */}
                    <div className="mb-3">
                        <p className="text-slate-400 mb-2 text-xs font-semibold">
                            Authentication
                        </p>
                        <div className="space-y-1">
                            {groupedShortcuts.auth.map((shortcut, index) => (
                                <button
                                    key={index}
                                    onClick={() =>
                                        handleNavigate(shortcut.path)
                                    }
                                    className={`w-full text-left px-3 py-2 rounded text-xs transition-colors flex items-center gap-2 ${
                                        isCurrentPath(shortcut.path)
                                            ? "bg-ecpi-blue/20 border border-ecpi-blue text-white"
                                            : "bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-300"
                                    }`}
                                    title={shortcut.description}
                                >
                                    {shortcut.icon}
                                    <span className="flex-1">
                                        {shortcut.label}
                                    </span>
                                    {isCurrentPath(shortcut.path) && (
                                        <span className="text-ecpi-blue">
                                            ●
                                        </span>
                                    )}
                                </button>
                            ))}
                        </div>
                    </div>
                    {/* Registration Forms */}
                    <div className="mb-3">
                        <p className="text-slate-400 mb-2 text-xs font-semibold">
                            Registration Forms
                        </p>
                        <div className="space-y-1">
                            {groupedShortcuts.enrollment.map(
                                (shortcut, index) => {
                                    if (
                                        shortcut.requiresUnverified &&
                                        !isTestEnvironment
                                    )
                                        return null;
                                    const isDisabled =
                                        (shortcut.requiresAuth && !user) ||
                                        (shortcut.requiresUnverified &&
                                            (!user || isVerified));
                                    const disabledReason = !user
                                        ? "Requires authentication"
                                        : shortcut.requiresUnverified &&
                                            isVerified
                                          ? "Requires unverified user (register new account or use unverified test user)"
                                          : shortcut.description;
                                    return (
                                        <button
                                            key={index}
                                            onClick={() =>
                                                !isDisabled &&
                                                handleNavigate(shortcut.path)
                                            }
                                            disabled={isDisabled}
                                            className={`w-full text-left px-3 py-2 rounded text-xs transition-colors flex items-center gap-2 ${
                                                isDisabled
                                                    ? "bg-slate-800/30 border border-slate-800 text-slate-600 cursor-not-allowed"
                                                    : isCurrentPath(
                                                            shortcut.path
                                                        )
                                                      ? "bg-ecpi-blue/20 border border-ecpi-blue text-white"
                                                      : "bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-300"
                                            }`}
                                            title={disabledReason}
                                        >
                                            {shortcut.icon}
                                            <span className="flex-1">
                                                {shortcut.label}
                                            </span>
                                            {isCurrentPath(shortcut.path) && (
                                                <span className="text-ecpi-blue">
                                                    ●
                                                </span>
                                            )}
                                            {isDisabled && (
                                                <span className="text-slate-600">
                                                    🔒
                                                </span>
                                            )}
                                        </button>
                                    );
                                }
                            )}
                        </div>
                    </div>
                    {/* Pages */}
                    <div className="mb-3">
                        <p className="text-slate-400 mb-2 text-xs font-semibold">
                            Dashboard Pages
                        </p>
                        <div className="space-y-1">
                            {groupedShortcuts.pages.map((shortcut, index) => {
                                const isDisabled =
                                    shortcut.requiresAuth && !user;
                                return (
                                    <button
                                        key={index}
                                        onClick={() =>
                                            !isDisabled &&
                                            handleNavigate(shortcut.path)
                                        }
                                        disabled={isDisabled}
                                        className={`w-full text-left px-3 py-2 rounded text-xs transition-colors flex items-center gap-2 ${
                                            isDisabled
                                                ? "bg-slate-800/30 border border-slate-800 text-slate-600 cursor-not-allowed"
                                                : isCurrentPath(shortcut.path)
                                                  ? "bg-ecpi-blue/20 border border-ecpi-blue text-white"
                                                  : "bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-300"
                                        }`}
                                        title={
                                            isDisabled
                                                ? "Requires authentication"
                                                : shortcut.description
                                        }
                                    >
                                        {shortcut.icon}
                                        <span className="flex-1">
                                            {shortcut.label}
                                        </span>
                                        {isCurrentPath(shortcut.path) && (
                                            <span className="text-ecpi-blue">
                                                ●
                                            </span>
                                        )}
                                        {isDisabled && (
                                            <span className="text-slate-600">
                                                🔒
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                    {/* Profile Steps */}
                    <div className="mb-3">
                        <p className="text-slate-400 mb-2 text-xs font-semibold">
                            Profile Form Steps
                        </p>
                        <div className="space-y-1">
                            {groupedShortcuts.forms.map((shortcut, index) => {
                                const isDisabled =
                                    shortcut.requiresAuth && !user;
                                return (
                                    <button
                                        key={index}
                                        onClick={() =>
                                            !isDisabled &&
                                            handleNavigate(shortcut.path)
                                        }
                                        disabled={isDisabled}
                                        className={`w-full text-left px-3 py-2 rounded text-xs transition-colors flex items-center gap-2 ${
                                            isDisabled
                                                ? "bg-slate-800/30 border border-slate-800 text-slate-600 cursor-not-allowed"
                                                : isCurrentPath(shortcut.path)
                                                  ? "bg-ecpi-blue/20 border border-ecpi-blue text-white"
                                                  : "bg-slate-800/60 hover:bg-slate-800 border border-slate-700 text-slate-300"
                                        }`}
                                        title={
                                            isDisabled
                                                ? "Requires authentication"
                                                : shortcut.description
                                        }
                                    >
                                        {shortcut.icon}
                                        <span className="flex-1">
                                            {shortcut.label}
                                        </span>
                                        {isCurrentPath(shortcut.path) && (
                                            <span className="text-ecpi-blue">
                                                ●
                                            </span>
                                        )}
                                        {isDisabled && (
                                            <span className="text-slate-600">
                                                🔒
                                            </span>
                                        )}
                                    </button>
                                );
                            })}
                        </div>
                    </div>
                </div>

                {/* Dev Tools / Email verification */}
                <div className="mt-3">
                    <p className="text-slate-400 mb-2 text-xs font-semibold">
                        Dev Tools
                    </p>
                    <div className="space-y-1">
                        {user && !isVerified ? (
                            <button
                                onClick={handleToggleEmailPopup}
                                disabled={showEmailPopupToggle}
                                className={`w-full text-left px-3 py-2 rounded text-xs transition-colors flex items-center gap-2 ${
                                    showEmailPopupToggle
                                        ? "bg-green-500/20 border border-green-500 text-green-300 cursor-not-allowed"
                                        : "bg-yellow-500/20 hover:bg-yellow-500/30 border border-yellow-500 text-yellow-300"
                                }`}
                                title="Manually trigger the email verification popup"
                            >
                                {showEmailPopupToggle ? (
                                    <>
                                        <div className="border-green-300 border-t-transparent h-3 w-3 animate-spin rounded-full border-2"></div>
                                        <span className="flex-1">
                                            Showing Email Popup...
                                        </span>
                                    </>
                                ) : (
                                    <>
                                        <Mail className="h-4 w-4" />
                                        <span className="flex-1">
                                            Show Email Verification
                                        </span>
                                    </>
                                )}
                            </button>
                        ) : user && isVerified ? (
                            <div className="bg-green-900/20 border-green-700 text-green-400 flex items-center gap-2 rounded border px-3 py-2 text-xs">
                                <span>✓</span>
                                <span className="flex-1">
                                    Email Already Verified
                                </span>
                            </div>
                        ) : (
                            <div className="bg-slate-800/30 border-slate-700 text-slate-500 flex items-center gap-2 rounded border px-3 py-2 text-xs">
                                <span>🔒</span>
                                <span className="flex-1">
                                    Show Email Verification
                                </span>
                                <span className="text-slate-600">
                                    Requires Auth
                                </span>
                            </div>
                        )}
                    </div>
                </div>

                {/* Authentication Info */}
                <div className="border-slate-700 border-t pt-4">
                    <p className="text-white mb-2 font-bold">
                        Authentication Info
                    </p>
                    <div className="text-slate-300 space-y-1 text-xs">
                        <div>
                            Cookie Secure:{" "}
                            <span className="text-slate-200">
                                {envInfo.isSecure ? "Enabled" : "Disabled"}
                            </span>
                        </div>
                        <div>
                            SameSite:{" "}
                            <span className="text-slate-200">
                                {envInfo.isSecure ? "None" : "Lax"}
                            </span>
                        </div>
                        <div>
                            Persistence:{" "}
                            <span className="text-slate-200">
                                {envInfo.isSecure ? "Full" : "Limited"}
                            </span>
                        </div>
                    </div>
                    <div className="mt-3">
                        <button
                            onClick={() => setShowAuthDiagnostics(true)}
                            className="w-full bg-blue-500 hover:bg-blue-600 text-white px-3 py-2 rounded text-xs transition-colors"
                        >
                            Open Auth Diagnostics
                        </button>
                        <p className="text-slate-400 mt-1 text-xs">
                            Detailed authentication state and troubleshooting
                            information
                        </p>
                    </div>
                </div>

                {/* Bottom minimized sections */}
                <div className="border-slate-700 space-y-3 border-t pt-4">
                    <div>
                        <p className="text-white mb-1 font-bold">
                            Development Mode - Email Capture Active
                        </p>
                        <p className="text-slate-300 text-xs">
                            Outgoing emails are intercepted and displayed here
                            for testing purposes.
                        </p>
                    </div>
                    <div>
                        <p className="text-white mb-2 font-bold">
                            Environment (Minimal)
                        </p>
                        <div className="text-slate-300 grid grid-cols-2 gap-x-4 gap-y-1 text-[11px]">
                            <div>
                                Mode:{" "}
                                <span className="text-slate-200">
                                    {import.meta.env.MODE}
                                </span>
                            </div>
                            <div>
                                Secure:{" "}
                                <span className="text-slate-200">
                                    {envInfo.isSecure ? "Yes" : "No"}
                                </span>
                            </div>
                            <div>
                                Host:{" "}
                                <span className="text-slate-200">
                                    {envInfo.hostname}
                                </span>
                            </div>
                            <div>
                                Port:{" "}
                                <span className="text-slate-200">
                                    {envInfo.port}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <AuthDiagnosticsPanel
                show={showAuthDiagnostics}
                onClose={() => setShowAuthDiagnostics(false)}
            />
        </>
    );
}
